import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, TextField, Typography } from '@mui/material';
import { Flex } from '../Flex';

/**
 * Compact jump-to-page component for AG Grid tables.
 * Can be placed in a toolbar or alongside existing pagination controls.
 *
 * @param {Object} props
 * @param {Object} props.gridApi - AG Grid API instance
 * @param {boolean} props.disabled - Disable the controls
 * @param {Function} props.onJump - Optional callback after jumping (receives page number)
 */
const JumpToPage = ({ gridApi, disabled = false, onJump }) => {
  const [pageInput, setPageInput] = useState('');

  const getTotalPages = useCallback(() => {
    if (!gridApi) return 1;
    return gridApi.paginationGetTotalPages() || 1;
  }, [gridApi]);

  const getCurrentPage = useCallback(() => {
    if (!gridApi) return 1;
    return (gridApi.paginationGetCurrentPage() || 0) + 1; // Convert to 1-indexed
  }, [gridApi]);

  const handleJump = useCallback(() => {
    const pageNum = parseInt(pageInput, 10);
    const totalPages = getTotalPages();

    if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= totalPages && gridApi) {
      // AG Grid uses 0-indexed pages
      gridApi.paginationGoToPage(pageNum - 1);
      setPageInput('');
      onJump?.(pageNum);
    }
  }, [gridApi, pageInput, getTotalPages, onJump]);

  const handleKeyPress = useCallback(
    (event) => {
      if (event.key === 'Enter') {
        handleJump();
      }
    },
    [handleJump]
  );

  return (
    <Flex.H sx={{ gap: 1, alignItems: 'center' }}>
      <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
        Jump to:
      </Typography>
      <TextField
        size="small"
        type="number"
        value={pageInput}
        onChange={(e) => setPageInput(e.target.value)}
        onKeyPress={handleKeyPress}
        disabled={disabled || !gridApi}
        placeholder="Page"
        inputProps={{
          min: 1,
          max: getTotalPages(),
          style: { width: '50px', textAlign: 'center' },
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            height: '32px',
          },
        }}
      />
      <Button
        size="small"
        variant="outlined"
        onClick={handleJump}
        disabled={disabled || !gridApi || !pageInput}
        sx={{ minWidth: '40px' }}
      >
        Go
      </Button>
    </Flex.H>
  );
};

JumpToPage.propTypes = {
  gridApi: PropTypes.object,
  disabled: PropTypes.bool,
  onJump: PropTypes.func,
};

export default JumpToPage;
